import { useCallback, useEffect, useRef, useState } from "react";

import { getLatestHomePops } from "../services/homePopsService";

const DEFAULT_LIMIT = 10;
const MAX_LIMIT = 50;

const normalizeLimit = (value) => {
  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed > 0
    ? Math.min(parsed, MAX_LIMIT)
    : DEFAULT_LIMIT;
};

const normalizePops = (items, limit) => {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .filter((pop) => {
      if (!pop || typeof pop !== "object") {
        return false;
      }

      const popId = Number(pop.popId);

      if (!Number.isSafeInteger(popId) || popId <= 0) {
        return false;
      }

      const key = String(popId);

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    })
    .slice(0, limit);
};

const normalizeResponse = (response) => {
  const data = response?.data ?? response;

  if (data?.success === false) {
    throw new Error(data.message || "Unable to load homepage Pops.");
  }

  if (Array.isArray(data)) {
    return {
      pops: data,
      total: data.length,
      pagination: {},
    };
  }

  if (!data || typeof data !== "object") {
    throw new Error("Invalid homepage Pops response.");
  }

  const items = Array.isArray(data.pops)
    ? data.pops
    : Array.isArray(data.popThumbs)
      ? data.popThumbs
      : null;

  if (!items) {
    console.error("Unexpected homepage Pops response:", data);

    throw new Error("Invalid homepage Pops response.");
  }

  return {
    pops: items,
    total: data.pagination?.total ?? data.total ?? items.length,
    pagination: data.pagination || {},
  };
};

export const useHomePops = ({
  limit = DEFAULT_LIMIT,
  autoLoad = true,
} = {}) => {
  const safeLimit = normalizeLimit(limit);

  const [pops, setPops] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(Boolean(autoLoad));
  const [error, setError] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const requestIdRef = useRef(0);

  const refresh = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }

    let active = true;

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();

    const loadPops = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getLatestHomePops({
          limit: safeLimit,
          offset: 0,
          signal: controller.signal,
        });

        if (
          !active ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        const data = normalizeResponse(response);

        const nextPops = normalizePops(data.pops, safeLimit);

        const responseTotal = Number(data.total);

        setPops(nextPops);

        setTotal(
          Number.isFinite(responseTotal) && responseTotal >= 0
            ? responseTotal
            : nextPops.length,
        );

        setHasMore(
          typeof data.pagination?.hasMore === "boolean"
            ? data.pagination.hasMore
            : nextPops.length < responseTotal,
        );
      } catch (requestError) {
        if (
          !active ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current ||
          requestError?.name === "CanceledError" ||
          requestError?.name === "AbortError" ||
          requestError?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error("Unable to load homepage Pops:", requestError);

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load the latest Pops.",
        );

        setPops([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        if (active && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void loadPops();

    return () => {
      active = false;
      controller.abort();
    };
  }, [safeLimit, autoLoad, refreshKey]);

  return {
    pops,
    total,
    hasMore,
    hasPops: pops.length > 0,

    loading,
    error,

    refresh,
    getHomePops: refresh,
  };
};

export default useHomePops;
